import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { Images } from "../../../../utils/imageConstants";
import { getPlayerImageUrl } from "../../../../helper/helper";
import NoDataFound from "../../../../components/common/NoDataFound";
import "../../tournaments/tournamentsDetails/TournamentStatsTab.scss";

// ─── Types (reuse same shape as tournament leaderboard) ───────────────────────

type TeamSnapshot = {
  teamSnapshotId?: string;
  teamSnapshotName?: string;
  teamName?: string;
};

type PlayerRef = Record<string, unknown> | null | undefined;

type BattingLeaderRow = {
  batsman?: PlayerRef;
  teamSnapshot?: TeamSnapshot;
  totalMatchesCount?: number;
  totalInningsCount?: number;
  average?: number;
  strikeRate?: number;
  totalRunsScored?: number;
  highestScore?: number;
  notOutCount?: number;
  centuriesCount?: number;
  halfCenturiesCount?: number;
  foursCount?: number;
  sixesCount?: number;
};

type BowlingLeaderRow = {
  bowler?: PlayerRef;
  teamSnapshot?: TeamSnapshot;
  totalMatchesCount?: number;
  totalInningsCount?: number;
  totalOvers?: number;
  totalWickets?: number;
  economy?: number;
  totalRunsCenceded?: number;
  totalMaidenOverCount?: number;
  fiveWicketsCount?: number;
  tenWicketsCount?: number;
};

type LeaderboardResponse = {
  statsType: string;
  battingStats: BattingLeaderRow[] | null;
  bowlingStats: BowlingLeaderRow[] | null;
};

// ─── Category definitions ─────────────────────────────────────────────────────

type StatKind = "batting" | "bowling";

type CategoryDef = {
  key: string;
  label: string;
  kind: StatKind;
  heroCol: string;
  sortFn: (a: any, b: any) => number;
};

const CATEGORIES: CategoryDef[] = [
  { key: "most-runs", label: "Most Runs", kind: "batting", heroCol: "runs", sortFn: (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0) },
  { key: "most-wickets", label: "Most Wickets", kind: "bowling", heroCol: "wkts", sortFn: (a, b) => (b.totalWickets ?? 0) - (a.totalWickets ?? 0) },
  { key: "best-averages", label: "Best Averages", kind: "batting", heroCol: "ave", sortFn: (a, b) => (b.average ?? 0) - (a.average ?? 0) },
  { key: "best-economy", label: "Best Economy", kind: "bowling", heroCol: "econ", sortFn: (a, b) => (a.economy ?? 999) - (b.economy ?? 999) },
  { key: "best-strike-rate", label: "Best Strike Rate", kind: "batting", heroCol: "sr", sortFn: (a, b) => (b.strikeRate ?? 0) - (a.strikeRate ?? 0) },
  { key: "highest-scorers", label: "Highest Scorers", kind: "batting", heroCol: "hs", sortFn: (a, b) => (b.highestScore ?? 0) - (a.highestScore ?? 0) },
  { key: "most-4s", label: "Most 4s", kind: "batting", heroCol: "4s", sortFn: (a, b) => (b.foursCount ?? 0) - (a.foursCount ?? 0) },
  { key: "most-6s", label: "Most 6s", kind: "batting", heroCol: "6s", sortFn: (a, b) => (b.sixesCount ?? 0) - (a.sixesCount ?? 0) },
  { key: "most-50s", label: "Most 50s", kind: "batting", heroCol: "50", sortFn: (a, b) => (b.halfCenturiesCount ?? 0) - (a.halfCenturiesCount ?? 0) },
  { key: "most-100s", label: "Most 100s", kind: "batting", heroCol: "100", sortFn: (a, b) => (b.centuriesCount ?? 0) - (a.centuriesCount ?? 0) },
  { key: "most-maidens", label: "Most Maidens", kind: "bowling", heroCol: "mdns", sortFn: (a, b) => (b.totalMaidenOverCount ?? 0) - (a.totalMaidenOverCount ?? 0) },
];

// ─── Card definitions (overview grid) ────────────────────────────────────────

type CardDef = {
  categoryKey: string;
  title: string;
  kind: StatKind;
  sortFn: (a: any, b: any) => number;
  mainStat: (r: any) => string;
  secondary: (r: any) => string;
  playerOf: (r: any) => PlayerRef;
  teamOf: (r: any) => TeamSnapshot | undefined;
};

const CARDS: CardDef[] = [
  {
    categoryKey: "most-runs",
    title: "MOST RUNS",
    kind: "batting",
    sortFn: (a, b) => (b.totalRunsScored ?? 0) - (a.totalRunsScored ?? 0),
    mainStat: (r) => fmtInt(r.totalRunsScored),
    secondary: (r) => `Innings: ${fmtInt(r.totalInningsCount)}   Average: ${fmtNum(r.average)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-wickets",
    title: "MOST WICKETS",
    kind: "bowling",
    sortFn: (a, b) => (b.totalWickets ?? 0) - (a.totalWickets ?? 0),
    mainStat: (r) => fmtInt(r.totalWickets),
    secondary: (r) => `Innings: ${fmtInt(r.totalInningsCount)}   Economy: ${fmtNum(r.economy)}`,
    playerOf: (r) => r.bowler,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "best-averages",
    title: "BEST AVERAGE",
    kind: "batting",
    sortFn: (a, b) => (b.average ?? 0) - (a.average ?? 0),
    mainStat: (r) => fmtNum(r.average),
    secondary: (r) => `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "best-strike-rate",
    title: "BEST STRIKE RATE",
    kind: "batting",
    sortFn: (a, b) => (b.strikeRate ?? 0) - (a.strikeRate ?? 0),
    mainStat: (r) => fmtNum(r.strikeRate),
    secondary: (r) => `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-maidens",
    title: "MOST MAIDENS",
    kind: "bowling",
    sortFn: (a, b) => (b.totalMaidenOverCount ?? 0) - (a.totalMaidenOverCount ?? 0),
    mainStat: (r) => fmtInt(r.totalMaidenOverCount),
    secondary: (r) => `Overs: ${fmtNum(r.totalOvers, 1)}   Wickets: ${fmtInt(r.totalWickets)}`,
    playerOf: (r) => r.bowler,
    teamOf: (r) => r.teamSnapshot,
  },
  {
    categoryKey: "most-6s",
    title: "MOST SIXES",
    kind: "batting",
    sortFn: (a, b) => (b.sixesCount ?? 0) - (a.sixesCount ?? 0),
    mainStat: (r) => fmtInt(r.sixesCount),
    secondary: (r) => `Innings: ${fmtInt(r.totalInningsCount)}   Runs: ${fmtInt(r.totalRunsScored)}`,
    playerOf: (r) => r.batsman,
    teamOf: (r) => r.teamSnapshot,
  },
];

// ─── Table columns ────────────────────────────────────────────────────────────

const BAT_COLS = [
  { key: "mat", label: "Mat", value: (r: BattingLeaderRow) => fmtInt(r.totalMatchesCount) },
  { key: "inn", label: "Inn", value: (r: BattingLeaderRow) => fmtInt(r.totalInningsCount) },
  { key: "no", label: "NO", value: (r: BattingLeaderRow) => fmtInt(r.notOutCount) },
  { key: "runs", label: "Runs", value: (r: BattingLeaderRow) => fmtInt(r.totalRunsScored) },
  { key: "hs", label: "HS", value: (r: BattingLeaderRow) => fmtInt(r.highestScore) },
  { key: "ave", label: "Ave", value: (r: BattingLeaderRow) => fmtNum(r.average) },
  { key: "sr", label: "SR", value: (r: BattingLeaderRow) => fmtNum(r.strikeRate) },
  { key: "100", label: "100", value: (r: BattingLeaderRow) => fmtInt(r.centuriesCount) },
  { key: "50", label: "50", value: (r: BattingLeaderRow) => fmtInt(r.halfCenturiesCount) },
  { key: "0", label: "0", value: (r: BattingLeaderRow) => fmtInt(r.notOutCount) },
  { key: "4s", label: "4s", value: (r: BattingLeaderRow) => fmtInt(r.foursCount) },
  { key: "6s", label: "6s", value: (r: BattingLeaderRow) => fmtInt(r.sixesCount) },
];

const BOWL_COLS = [
  { key: "mat", label: "Mat", value: (r: BowlingLeaderRow) => fmtInt(r.totalMatchesCount) },
  { key: "inn", label: "Inn", value: (r: BowlingLeaderRow) => fmtInt(r.totalInningsCount) },
  { key: "overs", label: "Overs", value: (r: BowlingLeaderRow) => fmtNum(r.totalOvers, 1) },
  { key: "wkts", label: "Wkts", value: (r: BowlingLeaderRow) => fmtInt(r.totalWickets) },
  { key: "econ", label: "Econ", value: (r: BowlingLeaderRow) => fmtNum(r.economy) },
  { key: "runs", label: "Runs", value: (r: BowlingLeaderRow) => fmtInt(r.totalRunsCenceded) },
  { key: "mdns", label: "Mdns", value: (r: BowlingLeaderRow) => fmtInt(r.totalMaidenOverCount) },
  { key: "5w", label: "5W", value: (r: BowlingLeaderRow) => fmtInt(r.fiveWicketsCount) },
  { key: "10w", label: "10W", value: (r: BowlingLeaderRow) => fmtInt(r.tenWicketsCount) },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function displayPlayerName(p: PlayerRef): string {
  if (!p || typeof p !== "object") return "—";
  const o = p as Record<string, unknown>;
  const first = [o.firstName, o.lastName, o.playerFirstName, o.playerLastName]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .join(" ")
    .trim();
  if (first) return first;
  const dn = o.displayName ?? o.name ?? o.playerName;
  if (typeof dn === "string" && dn.trim()) return dn.trim();
  return "—";
}

function playerImageUri(p: PlayerRef): string | null {
  if (!p || typeof p !== "object") return null;
  const o = p as Record<string, unknown>;
  const uri = o.imageUri ?? o.image ?? o.profileImage ?? o.avatarUrl;
  return typeof uri === "string" ? uri : null;
}

function teamLabel(ts: TeamSnapshot | null | undefined): string {
  if (!ts) return "—";
  const n = ts.teamSnapshotName ?? ts.teamName;
  return typeof n === "string" && n.trim() ? n.trim() : "—";
}

function fmtNum(n: number | undefined | null, digits = 2): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(digits);
}

function fmtInt(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  return String(Math.round(Number(n)));
}

function filterByTeam<T extends { teamSnapshot?: TeamSnapshot }>(
  rows: T[],
  teamSnapshotId: string,
): T[] {
  return rows.filter(
    (r) => r.teamSnapshot?.teamSnapshotId === teamSnapshotId,
  );
}

const CARD_PREVIEW = 3;

type NameSort = "none" | "asc" | "desc";

// ─── Component ────────────────────────────────────────────────────────────────

const TeamInfoStats: React.FC = () => {
  const teamSnapshotId = sessionStorage.getItem("teamSnapshotId") ?? "";
  const tournamentId = sessionStorage.getItem("tournamentId") ?? "";

  const [fullListCategory, setFullListCategory] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORIES[0].key);
  const [nameSort, setNameSort] = useState<NameSort>("none");

  const openFullList = (categoryKey: string) => {
    setActiveCategory(categoryKey);
    setFullListCategory(categoryKey);
    setNameSort("none");
  };

  const closeFullList = () => setFullListCategory(null);

  const handleCategoryChange = (categoryKey: string) => {
    setActiveCategory(categoryKey);
    setNameSort("none");
  };

  const toggleNameSort = () => {
    setNameSort((prev) =>
      prev === "none" ? "asc" : prev === "asc" ? "desc" : "none",
    );
  };

  // ── API ───────────────────────────────────────────────────────────────────
  const { data: battingData, isLoading: battingLoading, isError: battingError } = useQuery({
    queryKey: [QUERY_KEY.tournamentLeaderboard, tournamentId, "BATTING"],
    enabled: !!tournamentId,
    queryFn: async () => {
      const url = `${QUERY_KEY.tournamentLeaderboard}?tournamentId=${encodeURIComponent(tournamentId)}&filter=BATTING`;
      const res = await Api.get<LeaderboardResponse>(url);
      return res.data;
    },
  });

  const { data: bowlingData, isLoading: bowlingLoading, isError: bowlingError } = useQuery({
    queryKey: [QUERY_KEY.tournamentLeaderboard, tournamentId, "BOWLING"],
    enabled: !!tournamentId,
    queryFn: async () => {
      const url = `${QUERY_KEY.tournamentLeaderboard}?tournamentId=${encodeURIComponent(tournamentId)}&filter=BOWLING`;
      const res = await Api.get<LeaderboardResponse>(url);
      return res.data;
    },
  });

  // Filter to this team only
  const battingRows = useMemo(
    () => filterByTeam(battingData?.battingStats ?? [], teamSnapshotId),
    [battingData, teamSnapshotId],
  );
  const bowlingRows = useMemo(
    () => filterByTeam(bowlingData?.bowlingStats ?? [], teamSnapshotId),
    [bowlingData, teamSnapshotId],
  );

  const isLoading = battingLoading || bowlingLoading;
  const isError = battingError || bowlingError;

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!tournamentId || !teamSnapshotId) {
    return (
      <div className="ts-tab">
        <NoDataFound message="TEAM OR TOURNAMENT NOT FOUND" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="ts-tab">
        <div className="ts-tab__loading">
          <div className="ts-tab__spinner" />
          <span>Loading stats…</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="ts-tab">
        <div className="ts-tab__error" role="alert">
          Could not load stats. Please try again.
        </div>
      </div>
    );
  }

  if (battingRows.length === 0 && bowlingRows.length === 0) {
    return (
      <div className="ts-tab">
        <NoDataFound message="NO STATS FOR THIS TEAM YET" />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FULL-LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (fullListCategory !== null) {
    const category = CATEGORIES.find((c) => c.key === activeCategory) ?? CATEGORIES[0];

    const baseRows =
      category.kind === "batting"
        ? [...battingRows].sort(category.sortFn)
        : [...bowlingRows].sort(category.sortFn);

    const rows =
      nameSort === "none"
        ? baseRows
        : [...baseRows].sort((a, b) => {
          const na =
            category.kind === "batting"
              ? displayPlayerName(a.batsman)
              : displayPlayerName(a.bowler);
          const nb =
            category.kind === "batting"
              ? displayPlayerName(b.batsman)
              : displayPlayerName(b.bowler);
          const cmp = na.localeCompare(nb);
          return nameSort === "asc" ? cmp : -cmp;
        });

    const sortIcon =
      nameSort === "asc" ? " ↑" : nameSort === "desc" ? " ↓" : " ↕";

    return (
      <div className="ts-tab">
        {/* Pill filters */}
        <div className="ts-tab__pills">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`ts-tab__pill${activeCategory === cat.key ? " ts-tab__pill--active" : ""}`}
              onClick={() => handleCategoryChange(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="ts-table-wrap">
          <div className="ts-table-wrap__bar">
            <div className="ts-table-wrap__bar-left">
              <button
                type="button"
                className="ts-table-wrap__back"
                onClick={closeFullList}
                aria-label="Back to overview"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <h3 className="ts-table-wrap__title">{category.label}</h3>
            </div>
          </div>

          <div className="ts-table-wrap__scroll">
            {rows.length === 0 ? (
              <div className="ts-table-wrap__empty">No data for this category.</div>
            ) : category.kind === "batting" ? (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th
                      className="ts-table__player-th ts-table__player-th--sortable"
                      onClick={toggleNameSort}
                      title="Sort by player name"
                    >
                      Player<span className={`ts-table__sort-icon${nameSort !== "none" ? " ts-table__sort-icon--active" : ""}`}>{sortIcon}</span>
                    </th>
                    {BAT_COLS.map((col) => (
                      <th key={col.key} className={col.key === category.heroCol ? "ts-table__hero-th" : ""}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(rows as BattingLeaderRow[]).map((r, i) => {
                    const imgSrc = getPlayerImageUrl(playerImageUri(r.batsman)) || Images.player_image;
                    return (
                      <tr key={i}>
                        <td className="ts-table__player-td">
                          <img className="ts-table__avatar" src={imgSrc} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.player_image; }} />
                          <div className="ts-table__player-info">
                            <span className="ts-table__name">{displayPlayerName(r.batsman)}</span>
                            <span className="ts-table__team">{teamLabel(r.teamSnapshot)}</span>
                          </div>
                        </td>
                        {BAT_COLS.map((col) => (
                          <td key={col.key} className={col.key === category.heroCol ? "ts-table__hero-td" : ""}>{col.value(r)}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="ts-table">
                <thead>
                  <tr>
                    <th
                      className="ts-table__player-th ts-table__player-th--sortable"
                      onClick={toggleNameSort}
                      title="Sort by player name"
                    >
                      Player<span className={`ts-table__sort-icon${nameSort !== "none" ? " ts-table__sort-icon--active" : ""}`}>{sortIcon}</span>
                    </th>
                    {BOWL_COLS.map((col) => (
                      <th key={col.key} className={col.key === category.heroCol ? "ts-table__hero-th" : ""}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(rows as BowlingLeaderRow[]).map((r, i) => {
                    const imgSrc = getPlayerImageUrl(playerImageUri(r.bowler)) || Images.player_image;
                    return (
                      <tr key={i}>
                        <td className="ts-table__player-td">
                          <img className="ts-table__avatar" src={imgSrc} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.player_image; }} />
                          <div className="ts-table__player-info">
                            <span className="ts-table__name">{displayPlayerName(r.bowler)}</span>
                            <span className="ts-table__team">{teamLabel(r.teamSnapshot)}</span>
                          </div>
                        </td>
                        {BOWL_COLS.map((col) => (
                          <td key={col.key} className={col.key === category.heroCol ? "ts-table__hero-td" : ""}>{col.value(r)}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CARD GRID OVERVIEW
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="ts-tab">
      <div className="ts-tab__grid">
        {CARDS.map((card) => {
          const sourceRows = card.kind === "batting" ? battingRows : bowlingRows;
          const sorted = [...sourceRows].sort(card.sortFn);
          if (sorted.length === 0) return null;
          const preview = sorted.slice(0, CARD_PREVIEW);

          return (
            <div key={card.categoryKey} className="ts-card">
              <div className="ts-card__header">
                <span>{card.title}</span>
              </div>

              <div className="ts-card__body">
                {preview.map((r, i) => {
                  const playerRef = card.playerOf(r);
                  const imgSrc = getPlayerImageUrl(playerImageUri(playerRef)) || Images.player_image;
                  return (
                    <div key={i} className="ts-card__row">
                      <div className="ts-card__avatar-wrap">
                        <img
                          className="ts-card__avatar"
                          src={imgSrc}
                          alt={displayPlayerName(playerRef)}
                          onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.player_image; }}
                        />
                      </div>
                      <div className="ts-card__info">
                        <span className="ts-card__name">{displayPlayerName(playerRef)}</span>
                        <span className="ts-card__team">{teamLabel(card.teamOf(r))}</span>
                        <span className="ts-card__secondary">{card.secondary(r)}</span>
                      </div>
                      <div className="ts-card__value">{card.mainStat(r)}</div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="ts-card__view-all"
                onClick={() => openFullList(card.categoryKey)}
              >
                View full list <span className="ts-card__arrow">→</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamInfoStats;
